<template>
  <v-app
    id="portal"
    v-bind:class="{ collapsed: $store.getters.getSidebarOpen }"
  >
    <app-sidebar
      :windowWidth="windowWidth"
      :themeLoaded="themeLoaded"
    ></app-sidebar>
    <app-header :windowWidth="windowWidth"></app-header>

    <v-main>
      <v-container fluid>
        <div v-if="portalBannerVisible" class="portal-banner-wrap">
          <portal-banner
            :notification="portalBannerNotification"
            :visible="portalBannerVisible"
            @dismiss="dismissPortalBanner"
          />
        </div>
        <subscription-expired-modal
          v-model="subscriptionExpiredModalVisible"
          :message="subscriptionExpiredModalMessage"
          @acknowledge="acknowledgeSubscriptionExpiredNotice"
        />
        <v-row>
          <v-col>
            <slot />
          </v-col>
        </v-row>
      </v-container>
      <org-add-edit-modal />
    </v-main>
  </v-app>
</template>
<script type="text/javascript">
import { mapGetters, mapMutations } from 'vuex';
import constants from '../constants/resolutions-constants';
import Header from './shared/header.vue';
import Sidebar from './shared/sidebar.vue';
import OrgAddEditModal from "../components/organizations/organization-new-edit.vue";
import SubscriptionExpiredModal from '../components/common/SubscriptionExpiredModal.vue';
import { mobileCheck } from '../utils/mobileCheck';

import NotificationsService from '../services/notifications';
import PortalBanner from '../components/shared/portal-banner.vue';
import { selectPortalBannerNotification, getBannerDismissKey } from '../utils/portal-notification-banner';
import {
  getSubscriptionExpiredNoticeContent,
  isSubscriptionExpiredNoticeEnabled,
  markSubscriptionExpiredNoticeShownThisSession,
  wasSubscriptionExpiredNoticeShownThisSession,
} from '../utils/subscription-expired-notice';

export default {
  data() {
    return {
      themeLoaded: false,
      sm: constants.IPAD_PORTRAIT,
      windowWidth: document.documentElement.clientWidth,
      portalBannerNotification: null,
      portalBannerVisible: false,
      subscriptionExpiredModalVisible: false,
      subscriptionExpiredModalMessage: 'Your subscription has expired. Please contact your account manager.',
    };
  },
  components: {
    'app-header': Header,
    'app-sidebar': Sidebar,
    'org-add-edit-modal': OrgAddEditModal,
    'portal-banner': PortalBanner,
    'subscription-expired-modal': SubscriptionExpiredModal,
  },

  computed: {
    ...mapGetters('layout', ['getIsMobile',]),
    ...mapGetters('organization', ['currentOrganization']),
  },
  watch: {
    currentOrganization: {
      immediate: true,
      handler(org) {
        this.maybeShowSubscriptionExpiredNotice(org);
      },
    },
  },
  mounted() {
    this.setIsMobile(mobileCheck());

    if (this.getIsMobile && window.innerWidth <= constants.IPAD_PORTRAIT) {
      this.setIsMobileipadPortrait(true);
    } else {
      this.setIsMobileipadPortrait(false);
    }
    this.setDeviceWidth(window.innerWidth);
    window.addEventListener('resize', this.onResize);
    this.loadPortalBanner();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    ...mapMutations('layout', [
      'setDeviceWidth',
      'setIsMobile',
      'setIsMobileipadPortrait',
    ]),
    maybeShowSubscriptionExpiredNotice(org) {
      const debug = (() => {
        try {
          return !!localStorage.getItem('debugSubscriptionExpiredNotice');
        } catch (e) {
          return false;
        }
      })();
      if (debug) {
        console.log('[SubscriptionExpiredNotice] layout(mobile)/maybeShow start', {
          orgId: org && org.id,
          enabled: org && org.subscriptionExpiredNoticeEnabled,
          visible: this.subscriptionExpiredModalVisible,
          shownThisSession: org && org.id ? wasSubscriptionExpiredNoticeShownThisSession(org.id) : null,
        });
      }
      if (this.subscriptionExpiredModalVisible) return;
      if (!org || !org.id) {
        if (debug) console.log('[SubscriptionExpiredNotice] layout(mobile)/maybeShow exit: missing org');
        return;
      }
      if (!isSubscriptionExpiredNoticeEnabled(org)) {
        if (debug) console.log('[SubscriptionExpiredNotice] layout(mobile)/maybeShow exit: not enabled');
        return;
      }
      if (wasSubscriptionExpiredNoticeShownThisSession(org.id)) {
        if (debug) console.log('[SubscriptionExpiredNotice] layout(mobile)/maybeShow exit: already shown this session');
        return;
      }

      const content = getSubscriptionExpiredNoticeContent(org);
      this.subscriptionExpiredModalMessage = content.message;
      this.subscriptionExpiredModalVisible = true;
      if (debug) console.log('[SubscriptionExpiredNotice] layout(mobile)/maybeShow show modal', { orgId: org.id });
    },
    acknowledgeSubscriptionExpiredNotice() {
      const org = this.currentOrganization;
      if (org && org.id) {
        markSubscriptionExpiredNoticeShownThisSession(org.id);
      }
      this.subscriptionExpiredModalVisible = false;
    },
    async loadPortalBanner() {
      try {
        const notifications = await NotificationsService.getActiveNotifications();
        const selected = selectPortalBannerNotification(notifications);
        if (!selected) {
          this.portalBannerNotification = null;
          this.portalBannerVisible = false;
          return;
        }

        const dismissKey = getBannerDismissKey(selected.id);
        const dismissed = sessionStorage.getItem(dismissKey);
        if (dismissed) {
          this.portalBannerNotification = selected;
          this.portalBannerVisible = false;
          return;
        }

        this.portalBannerNotification = selected;
        this.portalBannerVisible = true;
      } catch (e) {
        this.portalBannerNotification = null;
        this.portalBannerVisible = false;
      }
    },
    dismissPortalBanner() {
      try {
        const id = this.portalBannerNotification && this.portalBannerNotification.id;
        if (id) {
          sessionStorage.setItem(getBannerDismissKey(id), '1');
        }
      } catch (e) {
        // ignore storage access errors
      }
      this.portalBannerVisible = false;
    },
    onResize(event) {
      this.setIsMobile(mobileCheck());
      if (
        this.getIsMobile &&
        event.target.innerWidth <= constants.IPAD_PORTRAIT
      ) {
        this.setIsMobileipadPortrait(true);
      } else {
        this.setIsMobileipadPortrait(false);
      }
      this.windowWidth = event.target.innerWidth;
      this.setDeviceWidth(event.target.innerWidth);
    },
  },
};
</script>

<style lang="scss" type="text/css" scoped>
.portal-banner-wrap {
  margin: 12px 0;
}
</style>